import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle token expiry
api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // A 401 means no Authorization header was sent at all (see verifyToken
    // middleware) — there's nothing to refresh, the session is simply gone
    // (token cleared, or a request raced a logout). Recover the same way an
    // unrecoverable 403 does below, instead of leaving every open tab's
    // background polls silently failing against a dead session forever.
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('lastRoute');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // If error is not 403 or already retried, reject
    if (status !== 403 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Check if error is due to token expiry
    if (error.response?.data?.expired) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No token available');
        }

        const refreshResponse = await fetch(`${API_URL}/auth/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const refreshData = await refreshResponse.json();

        if (refreshData.success && refreshData.token) {
          // Store new token
          localStorage.setItem('token', refreshData.token);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${refreshData.token}`;
          return api(originalRequest);
        } else {
          // Refresh failed - logout
          localStorage.removeItem('token');
          localStorage.removeItem('lastRoute');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed - logout
        localStorage.removeItem('token');
        localStorage.removeItem('lastRoute');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // For other 403 errors
    if (error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('lastRoute');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Auth API
export const requestOTP = async (email) => {
  const response = await api.post('/auth/request-otp', { email });
  return response.data;
};

export const verifyOTP = async (email, otp, organisationId) => {
  const response = await api.post('/auth/verify-otp', { email, otp, organisationId });
  return response.data;
};

export const verifyToken = async () => {
  const response = await api.get('/auth/verify-token');
  return response.data;
};

export const getUserOrganisations = async (email) => {
  const response = await api.get('/auth/organisations', { params: { email } });
  return response.data;
};

// export const refreshTokenAPI = async () => {
//   const token = localStorage.getItem('token');
//   if (!token) {
//     throw new Error('No token available');
//   }
//   const response = await api.post('/auth/refresh-token');
//   return response.data;
// };
// Add this to your api.js exports
export const refreshTokenAPI = async () => {
  const response = await api.post('/auth/refresh-token');
  return response.data;
};

// Organisation API
export const getOrganisations = async () => {
  const response = await api.get('/organisations');
  return response.data;
};

export const getOrganisationDetail = async (id) => {
  const response = await api.get(`/organisations/${id}`);
  return response.data;
};

export const getOrganisationGroups = async (id) => {
  const response = await api.get(`/organisations/${id}/groups`);
  return response.data;
};

export const getOrganisationAdmins = async (id) => {
  const response = await api.get(`/organisations/${id}/admins`);
  return response.data;
};

export const createOrganisation = async (data) => {
  const response = await api.post('/organisations', data);
  return response.data;
};

export const updateOrganisation = async (id, data) => {
  const response = await api.put(`/organisations/${id}`, data);
  return response.data;
};

export const deleteOrganisation = async (id) => {
  const response = await api.delete(`/organisations/${id}`);
  return response.data;
};

// Group API
export const getGroupDetail = async (id) => {
  const response = await api.get(`/groups/${id}`);
  return response.data;
};

export const createGroup = async (data) => {
  const response = await api.post('/groups', data);
  return response.data;
};

export const updateGroup = async (id, data) => {
  const response = await api.put(`/groups/${id}`, data);
  return response.data;
};

export const deleteGroup = async (id) => {
  const response = await api.delete(`/groups/${id}`);
  return response.data;
};

export const getGroupCountries = async () => {
  const response = await api.get('/groups/meta/countries');
  return response.data;
};

export const getLanguagesList = async () => {
  const response = await api.get('/groups/meta/languages');
  return response.data;
};

export const getGroupLanguage = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/language`);
  return response.data;
};

export const setGroupLanguage = async (groupId, languageId) => {
  const response = await api.put(`/groups/${groupId}/language`, { language_id: languageId });
  return response.data;
};

// Group Admins API
export const getGroupAdmins = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/admins`);
  return response.data;
};

export const createGroupAdmin = async (groupId, data) => {
  const response = await api.post(`/groups/${groupId}/admins`, data);
  return response.data;
};

export const updateGroupAdmin = async (groupId, adminId, data) => {
  const response = await api.put(`/groups/${groupId}/admins/${adminId}`, data);
  return response.data;
};

export const deleteGroupAdmin = async (groupId, adminId) => {
  const response = await api.delete(`/groups/${groupId}/admins/${adminId}`);
  return response.data;
};

// Advert Library API
export const getAdvertLibrary = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/adverts`);
  return response.data;
};

export const createAdvertLibraryItem = async (groupId, { file, link }) => {
  const formData = new FormData();
  if (file) formData.append('image', file);
  if (link) formData.append('link', link);
  const response = await api.post(`/groups/${groupId}/adverts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateAdvertLibraryItem = async (groupId, advertId, { file, link }) => {
  const formData = new FormData();
  if (file) formData.append('image', file);
  if (link !== undefined) formData.append('link', link);
  const response = await api.put(`/groups/${groupId}/adverts/${advertId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteAdvertLibraryItem = async (groupId, advertId) => {
  const response = await api.delete(`/groups/${groupId}/adverts/${advertId}`);
  return response.data;
};

// Org Advert API — the organisation's own shared "B - ORG AD" template
export const getOrgAdvertTemplate = async () => {
  const response = await api.get('/admin/org-advert-template');
  return response.data;
};

export const saveOrgAdvertTemplate = async ({ file, link }) => {
  const formData = new FormData();
  if (file) formData.append('image', file);
  if (link !== undefined) formData.append('link', link);
  const response = await api.post('/admin/org-advert-template', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// Live Adverts API
export const getLiveAdverts = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/live-adverts`);
  return response.data;
};

export const setLiveAdvertSlot = async (groupId, data) => {
  const response = await api.put(`/groups/${groupId}/live-adverts`, data);
  return response.data;
};

// Events API
export const getEvents = async (groupId) => {
  const response = await api.get(`/groups/${groupId}/events`);
  return response.data;
};

export const createEvent = async (groupId, data) => {
  const response = await api.post(`/groups/${groupId}/events`, data);
  return response.data;
};

export const updateEvent = async (groupId, eventId, data) => {
  const response = await api.put(`/groups/${groupId}/events/${eventId}`, data);
  return response.data;
};

export const deleteEvent = async (groupId, eventId) => {
  const response = await api.delete(`/groups/${groupId}/events/${eventId}`);
  return response.data;
};

// Admin API
export const getOrganisationAdminsList = async () => {
  const response = await api.get('/admin/admins');
  return response.data;
};

export const addAdminUser = async (data) => {
  const response = await api.post('/admin/admins', data);
  return response.data;
};

export const updateAdminUser = async (id, data) => {
  const response = await api.put(`/admin/admins/${id}`, data);
  return response.data;
};

export const deleteAdminUser = async (id) => {
  const response = await api.delete(`/admin/admins/${id}`);
  return response.data;
};

// Logs API
export const getLogs = async (params) => {
  const response = await api.get('/admin/logs', { params });
  return response.data;
};

export const getLogStats = async () => {
  const response = await api.get('/admin/logs/stats');
  return response.data;
};

// Countries API (WikiWaste Search)
export const getCountries = async () => {
  const response = await api.get('/countries');
  return response.data;
};

export default api;